import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { controlsOfType, distinctTypes, readAuswahllisten, readContentControls } from "@/office/word/formulare";

export const lektionSteuerelemente: Lesson = {
  id: "steuerelemente",
  title: "Die verschiedenen Steuerelemente",
  summary: "Kontrollkästchen, Auswahllisten und Datumsauswahl – und wann welches passt.",
  durationMinutes: 9,
  learningGoals: [
    "Die wichtigsten Steuerelementtypen unterscheiden",
    "Auswahllisten mit Einträgen füllen",
    "Den passenden Typ für eine Eingabe wählen",
  ],
  steps: [
    {
      id: "typen",
      title: "Welches Element wofür?",
      explanation: [
        "Die Gruppe „Steuerelemente“ bietet mehrere Typen, und die Wahl entscheidet " +
          "darüber, wie gut ein Formular ausfüllbar ist.",
        "**Nur-Text** erlaubt einfachen Text ohne Formatierung – die richtige Wahl für " +
          "Namen, Nummern und alles, was später ausgewertet wird. **Rich-Text** erlaubt " +
          "Formatierung und mehrere Absätze, sinnvoll für Freitextfelder.",
        "**Kontrollkästchen** für Ja/Nein-Angaben. **Dropdownliste** für eine Auswahl aus " +
          "festen Vorgaben – die ausfüllende Person kann nichts anderes eintragen. " +
          "**Kombinationsfeld** ist dasselbe, erlaubt aber zusätzlich freie Eingabe. " +
          "**Datumsauswahl** öffnet einen Kalender und stellt sicher, dass ein gültiges " +
          "Datum in einheitlichem Format entsteht.",
        "Die Faustregel: Je stärker du die Eingabe einschränkst, desto verlässlicher sind " +
          "die Daten. Ein Freitextfeld für ein Datum bringt dir „3.4.“, „03.04.2025“ und " +
          "„nächsten Donnerstag“ zurück.",
      ],
      keyPoints: [
        "Nur-Text für auswertbare Angaben, Rich-Text für Freitext.",
        "Dropdownliste schränkt ein, Kombinationsfeld erlaubt zusätzlich freie Eingabe.",
        "Datumsauswahl sichert ein einheitliches Format.",
      ],
      task: {
        instruction:
          "Füge deinem Formular mindestens drei verschiedene Arten von Steuerelementen " +
          "hinzu – zum Beispiel ein Textfeld, ein Kontrollkästchen und eine Dropdownliste.",
        hint: "Die Symbole liegen nebeneinander in der Gruppe „Steuerelemente“ der Entwicklertools.",
        check: async () => {
          const felder = await readContentControls();
          if (felder.length === 0) {
            return fail(
              "Es wurde noch kein Inhaltssteuerelement gefunden.",
              "Blende zuerst die Entwicklertools ein.",
            );
          }

          const typen = distinctTypes(felder);
          return fromCriteria(
            [
              criterion(`Mindestens drei Steuerelemente (gefunden: ${felder.length})`, felder.length >= 3),
              criterion(
                `Mindestens drei verschiedene Typen (gefunden: ${typen.join(", ")})`,
                typen.length >= 3,
              ),
            ],
            {
              success: "Ein Formular mit passenden Feldtypen – so werden die Daten brauchbar.",
              failure: "Da fehlt noch Abwechslung.",
              hint: "Drei Textfelder zählen als ein Typ. Nimm zum Beispiel zusätzlich ein Kontrollkästchen und eine Dropdownliste.",
            },
          );
        },
      },
    },
    {
      id: "auswahl-fuellen",
      title: "Auswahllisten füllen",
      explanation: [
        "Eine frisch eingefügte Dropdownliste ist leer – genauer gesagt enthält sie nur " +
          "den Platzhalter „Element auswählen“. Die Einträge musst du selbst anlegen.",
        "Dazu klickst du das Element an und öffnest „Eigenschaften“. Im unteren Bereich " +
          "des Dialogs steht die Liste der Einträge mit den Schaltflächen „Hinzufügen“, " +
          "„Ändern“, „Entfernen“ sowie Pfeilen zum Sortieren.",
        "Jeder Eintrag hat einen Anzeigenamen und einen Wert. Meist sind beide gleich; " +
          "unterschiedlich werden sie, wenn im Formular etwas Lesbares stehen soll " +
          "(„Vertrieb Nord“), die Auswertung aber einen Code erwartet („VN“).",
      ],
      keyPoints: [
        "Entwicklertools → Eigenschaften → Bereich für die Listeneinträge.",
        "Anzeigename und Wert können sich unterscheiden.",
        "Die Reihenfolge lässt sich im Dialog sortieren.",
      ],
      task: {
        instruction:
          "Füge deinem Formular eine Dropdownliste oder ein Kombinationsfeld hinzu und " +
          "trage dort mindestens drei Auswahlmöglichkeiten ein.",
        hint: "Die Einträge legst du im Eigenschaften-Dialog über „Hinzufügen“ an.",
        check: async () => {
          const felder = await readContentControls();
          const auswahl = controlsOfType(felder, "DropDownList", "ComboBox");
          const listen = await readAuswahllisten();
          const gefuellt = listen?.filter((liste) => liste.eintraege >= 3) ?? [];

          return fromCriteria(
            [
              criterion(
                `Eine Auswahlliste ist vorhanden (gefunden: ${auswahl.length})`,
                auswahl.length >= 1,
              ),
              ...(listen === null
                ? []
                : [criterion(`Eine Liste hat mindestens drei Einträge (gefunden: ${Math.max(0, ...listen.map((l) => l.eintraege))})`, gefuellt.length > 0)]),
            ],
            {
              success:
                listen === null
                  ? "Die Auswahlliste steht. Ob wirklich drei Einträge drin sind, siehst du beim Aufklappen – " +
                    "diese Word-Version liefert die Einträge erst ab WordApi 1.9."
                  : "Die Auswahlliste steht und bietet genug Einträge zur Auswahl.",
              failure: "Es fehlt noch eine Auswahlliste mit mindestens drei Einträgen.",
              hint: "Einträge ergänzt du über Entwicklertools → Eigenschaften → Hinzufügen.",
            },
          );
        },
      },
    },
  ],
};
